import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStore,
  faMapMarkerAlt,
  faIdBadge,
  faUser,
  faPhone,
  faEnvelope,
  faCreditCard,
  faReceipt,
  faPrint,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faHourglassHalf,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";

const PaymentDetails = ({ payment, onClose }) => {
  // Get status badge configuration
  const getStatusBadge = (status) => {
    const statusConfig = {
      Paid: {
        className: "bg-green-600 text-white border-green-200",
        icon: faCheckCircle,
        iconColor: "text-green-600",
        dotColor: "bg-green-600",
        bgColor: "bg-white",
        borderColor: "border-green-400",
        label: "Paid",
        description: "Payment completed successfully",
      },
      Pending: {
        className: "bg-orange-600 text-white border-orange-200",
        icon: faHourglassHalf,
        iconColor: "text-orange-600",
        dotColor: "bg-orange-500",
        bgColor: "bg-white",
        borderColor: "border-orange-200",
        label: "Pending",
        description: "Awaiting payment confirmation",
      },
      Overdue: {
        className: "bg-red-500 text-white border-red-200",
        icon: faExclamationTriangle,
        iconColor: "text-red-500",
        dotColor: "bg-red-500",
        bgColor: "bg-white",
        borderColor: "border-red-200",
        label: "Overdue",
        description: "Payment is overdue",
      },
      Finish: {
        className: "bg-gray-600 text-white border-gray-200",
        icon: faTimesCircle,
        iconColor: "text-gray-600",
        dotColor: "bg-gray-600",
        bgColor: "bg-white",
        borderColor: "border-gray-200",
        label: "Finish",
        description: "Subscription period ended",
      },
    };

    return statusConfig[status] || statusConfig.Pending;
  };

  // Format currency
   const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sw-TZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) + " TSH";
  };

  // Info Item Component
  const InfoItem = ({ icon, label, value, highlight = false }) => (
    <div
      className={`flex items-start gap-3 p-3 rounded-md border transition-all duration-200 ${
        highlight
          ? "bg-blue-50 border-blue-200"
          : "bg-gray-50 border-gray-100 hover:bg-gray-100"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          highlight ? "bg-blue-100" : "bg-white border border-gray-200"
        }`}
      >
        <FontAwesomeIcon
          icon={icon}
          className={`h-4 w-4 ${highlight ? "text-blue-700" : "text-gray-600"}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm uppercase tracking-wider text-gray-500 font-medium">
          {label}
        </p>
        <p className="text-sm font-medium text-gray-900 truncate">
          {value || "-"}
        </p>
      </div>
    </div>
  );

  const status = getStatusBadge(payment?.status);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Payment details"
      maxWidth="sm:max-w-2xl"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Close
          </Button>
          <Button className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6">
            <FontAwesomeIcon icon={faPrint} />
            Print receipt
          </Button>
        </div>
      }
    >
      {payment && (
        <div className="flex flex-col gap-4">
          {/* Info Banner */}
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faReceipt}
                className="text-blue-800 h-4 w-4"
              />
            </div>
            <div>
              <p className="text-sm text-black font-semibold">
                Transaction {payment.transactionId}
              </p>
              <p className="text-sm text-gray-700 font-medium">
                Payment details and information
              </p>
            </div>
          </div>

          {/* Status Banner */}

          {/* Shop Information */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 border-b border-gray-200 pb-2">
              <FontAwesomeIcon
                icon={faStore}
                className="text-blue-700 h-4 w-4"
              />
              Shop information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <InfoItem
                icon={faStore}
                label="Shop Name"
                value={payment.shopName}
                highlight={true}
              />
              <InfoItem
                icon={faMapMarkerAlt}
                label="Location"
                value={payment.shopLocation}
              />
              <InfoItem
                icon={faIdBadge}
                label="Control Number"
                value={payment.controllerNumber}
                highlight={true}
              />
            </div>
          </div>

          {/* Owner Information */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 border-b border-gray-200 pb-2">
              <FontAwesomeIcon
                icon={faUser}
                className="text-blue-600 h-3.5 w-3.5"
              />
              Owner information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <InfoItem
                icon={faUser}
                label="Owner Name"
                value={payment.ownerName}
                highlight={true}
              />
              <InfoItem
                icon={faPhone}
                label="Phone Number"
                value={payment.ownerPhone}
              />
              <InfoItem
                icon={faEnvelope}
                label="Email Address"
                value={payment.ownerEmail}
              />
            </div>
          </div>

          {/* Payment Information */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 border-b border-gray-200 pb-2">
              <FontAwesomeIcon
                icon={faCreditCard}
                className="text-blue-600 h-3.5 w-3.5"
              />
              Payment information
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                  Amount
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {formatCurrency(payment.subscriptionFee)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                  Payment Date
                </p>
                <p className="text-xs font-semibold text-gray-900">
                  {new Date(payment.paymentDate).toLocaleDateString()}
                </p>
              </div>
              <div
                className={`bg-gray-50 rounded-lg p-3 text-center border border-gray-200`}
              >
                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                  Due Date
                </p>
                <p
                  className={`text-xs font-semibold ${
                    payment.status === "Overdue"
                      ? "text-red-600"
                      : payment.status === "Paid"
                        ? "text-green-600"
                        : "text-orange-600"
                  }`}
                >
                  {new Date(payment.dueDate).toLocaleDateString()}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-xs uppercase tracking-wider text-gray-600 font-medium">
                  Payment Method
                </p>
                <p className="text-xs font-semibold text-gray-900">
                  {payment.paymentMethod}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-xs uppercase tracking-wider text-gray-600 font-medium">
                  Plan
                </p>
                <Badge
                  variant="outline"
                  className="bg-blue-800 text-white border-blue-200 text-[10px]"
                >
                  {payment.plan}
                </Badge>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                <p className="text-xs uppercase tracking-wider text-gray-600 font-medium">
                  Type
                </p>
                <p className="text-xs font-semibold text-gray-900">
                  {payment.paymentType}
                </p>
              </div>
            </div>
          </div>

          <div
            className={`rounded-md p-4 ${status.bgColor} border ${status.borderColor}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <span
                    className={`inline-block w-3 h-3 rounded-full ${status.dotColor} animate-pulse`}
                  ></span>
                  <FontAwesomeIcon
                    icon={status.icon}
                    className={`h-4 w-4 ${status.iconColor} absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm`}
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Status:{" "}
                    <span className={status.iconColor}>{payment.status}</span>
                  </p>
                  <p className="text-xs text-gray-700 font-medium">
                    {status.description}
                  </p>
                </div>
              </div>
              <Badge
                variant="outline"
                className={`${status.className} text-xs font-medium px-3 py-1`}
              >
                {payment.status}
              </Badge>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PaymentDetails;
